import { Injectable } from '@angular/core';
import { MockupScene } from '@api-ui-app/src/app/mockup-studio/types/mockup-studio-scenes';
import { OrderItem } from '@api-ui-app/src/app/orders/lib/order-item';
import { SubscriptionsFacade } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.facade';
import { Design } from '@gelato-api-ui/core/designs/design';
import { DesignsApiService } from '@gelato-api-ui/core/designs/services/designs-api.service';
import {
  EditorApparelPreviewScenes,
  PremiumPreviewSceneRegExp,
} from '@gelato-api-ui/core/preflight/apparel-preview-scenes';
import { Scene } from '@gelato-api-ui/core/preflight/scene';
import { ProductPreviewScenesService } from '@gelato-api-ui/core/preflight/services/product-preview-scenes.service';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { CheckoutProductPreviewUrlProvider } from '@api-ui-app/src/app/checkout/services/checkout-product-preview-url.provider';

export const CHECKOUT_PREVIEW_SIZE = 1500;

@Injectable({ providedIn: 'root' })
export class CheckoutItemPreviewsService {
  readonly isGelatoPremium$ = this.subscriptionsFacade.isPaidSubscriptionActive$;

  constructor(
    private readonly designsApiService: DesignsApiService,
    private readonly productPreviewScenesService: ProductPreviewScenesService,
    private readonly checkoutProductPreviewUrlProvider: CheckoutProductPreviewUrlProvider,
    private readonly subscriptionsFacade: SubscriptionsFacade,
  ) {}

  getPreviews(item: OrderItem) {
    return combineLatest([
      this.designsApiService.get(item.designId),
      this.productPreviewScenesService.get(item.productUid),
      this.isGelatoPremium$,
    ]).pipe(
      filter(([_, scenes, isGelatoPremium]) => !!scenes && isGelatoPremium != null),
      switchMap(([design, scenes, isGelatoPremium]) => {
        const scenesListFiltered = this.getFilteredScenes(scenes, isGelatoPremium);

        if (scenesListFiltered?.length) {
          return forkJoin(scenesListFiltered.map(scene => this.getPreviewUrlByScene(scene, item, design?.structure)));
        } else {
          return of([]);
        }
      }),
    );
  }

  getDesign(item: OrderItem): Observable<Design> {
    return this.designsApiService.get(item.designId);
  }

  getPreviewsFromDesign(design: Design, item: OrderItem) {
    return combineLatest([this.productPreviewScenesService.get(item.productUid), this.isGelatoPremium$]).pipe(
      filter(([scenes, isGelatoPremium]) => !!scenes && isGelatoPremium != null),
      switchMap(([scenes, isGelatoPremium]) => {
        const scenesListFiltered = this.getFilteredScenes(scenes, isGelatoPremium);

        if (scenesListFiltered?.length) {
          return forkJoin(scenesListFiltered.map(scene => this.getPreviewUrlByScene(scene, item, design?.structure)));
        } else {
          return of([]);
        }
      }),
    );
  }

  private getPreviewUrlByScene(scene: string, item: OrderItem, designStructure: string): Observable<string> {
    return this.checkoutProductPreviewUrlProvider.get(scene, item, designStructure);
  }

  private getFilteredScenes(scenes: MockupScene[], isGelatoPremium: boolean): string[] {
    const scenesList = isGelatoPremium ? scenes : scenes.filter(s => !PremiumPreviewSceneRegExp.test(s.id));
    // GAPI-15227: 4 is the limit for all the previews to show
    return scenesList
      ?.map(s => s.id)
      .filter(scene => !EditorApparelPreviewScenes.includes(scene as Scene))
      ?.slice(0, 4);
  }
}
